// Theme preference.
//
// Three states, not two: "system" is the default and the one most people
// should stay on, with light/dark as explicit overrides. A two-state toggle
// has no way back to following the OS once it has been touched.
//
// Stored in localStorage rather than sessionStorage — a display preference
// should survive closing the tab, unlike the session token next to it.

export type Theme = "system" | "light" | "dark";

const KEY = "otc.theme";

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    // Private windows and blocked site data throw on access rather than
    // returning null, so the read itself has to be guarded.
    return "system";
  }
}

export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", t);

  try {
    if (t === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, t);
  } catch {
    /* preference simply won't persist; the applied theme is unaffected */
  }
}

/** What the user is actually looking at right now, system resolved. */
export function effective(t: Theme): "light" | "dark" {
  if (t !== "system") return t;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
